/**
 * ngPrometReport — shared, pure render core for the Nova Gorica Promet report.
 *
 * Extracted from NgPrometPanel so the sidebar panel AND the standalone
 * `/ng-report` page render byte-identical totals / tables / trend chart from the
 * same `GET …/promet/timeseries` response. Everything here is a pure function
 * (data → HTML string or ECharts option) — no DOM, no chart-instance lifecycle,
 * no fetch. The ECharts instance (init/dispose/ResizeObserver) stays with each
 * caller; this module only builds the option object.
 *
 * Issue #359 (open report in a new tab). Keep in sync: the panel and the page
 * both import from here — there is no second copy of this logic.
 */

import type { EChartsOption } from 'echarts';
import type {
  TimeseriesResponse,
  PerSensorRow,
  TrendSeriesShape,
} from '../../../../../../shared/types/ngPromet.js';
import { formatLjubljanaTs, ljubljanaDayStartIso } from '../../../../../../shared/utils/ljubljanaTime.js';
import { formatDayFirst } from '../../../../../../shared/utils/dayFirstDate.js';
import {
  ljubljanaAxisLabel,
  ljubljanaAxisPointerLabel,
  ljubljanaTooltipFormatter,
} from '../ngChartTime.js';

// ----------------------------------------------------------------------------
// Slovenian copy — the render-specific labels shared by panel + report page.
// (Control-only labels stay in NgPrometPanel.)
// ----------------------------------------------------------------------------
export const PROMET_COPY = {
  empty: 'V izbranem obdobju ni podatkov.',
  totalsTitle: 'Skupaj v obdobju',
  totalsSensorsLabel: (n: number) => `Senzorji (${n} ${n === 1 ? 'izbran' : n < 5 ? 'izbrana' : 'izbranih'})`,
  perSensorTitle: 'Po senzorjih',
  trendTitle: 'Časovna vrsta',
  trendDaily: 'Dnevno (zlepljeno na strani odjemalca)',
  trendHourly: 'Urno',
  motorUnit: 'voz.',
  chartEmpty: 'Ni točk za prikaz v izbranem obdobju.',
  chartMarkRef: 'ref',
  chartMarkCmp: 'primerjava',
  tableSensor: 'Senzor',
  tableObdobje: 'Obdobje (L / D)',
  tableTotal: 'Skupaj',
  tableRefAvg: 'Ref povp.',
  tableCmp: 'Primerjava',
  tableDeltaAbs: 'Δ abs',
  tableDeltaPct: 'Δ %',
};

// A sensor MONG took down: still selectable for its history, marked wherever
// its name shows (list, table, chart legend, report page).
export const FORMER_SENSOR_SUFFIX = '(pretekli)';

// What a series or a row reads when its sensor is not in the answer. It cannot
// happen (rows and series come from the same sensor set); an internal id is
// never the fallback.
export const UNKNOWN_SENSOR_LABEL = 'Neznan števec';

// The chart legend shows this many characters of a name; the rest is in the
// legend's tooltip, the chart's own tooltip and the table.
export const LEGEND_NAME_MAX = 32;

// Mirror of server EXPORT_ROW_CAP in src/server/schemas/ng-promet.schemas.ts.
// The client-side preflight disables export buttons when the estimated row
// count exceeds this; the server enforces the same cap as defense-in-depth.
// Shared so the panel AND the /ng-report page apply one identical cap (#359 F7).
export const EXPORT_ROW_CAP_CLIENT = 250_000;

export type PrometInterval = 'daily' | 'hourly';

// ----------------------------------------------------------------------------
// Export-cap estimate — hourly buckets × selected sensors.
// ----------------------------------------------------------------------------
export function estimateExportRows(fromIso: string, toIso: string, sensorCount: number): number {
  const fromMs = new Date(fromIso).getTime();
  const toMs = new Date(toIso).getTime();
  if (!Number.isFinite(fromMs) || !Number.isFinite(toMs) || toMs <= fromMs) return 0;
  const buckets = Math.ceil((toMs - fromMs) / 1000 / 3600);
  return buckets * sensorCount;
}

// ----------------------------------------------------------------------------
// Result HTML — totals + per-sensor + trend placeholder. The zone figures are
// the zone view's (#711, ngPrometZoneReport.ts); the old route's per_zone /
// transition_summary are not shown anywhere any more.
// `actionsHtml` (export row) is injected after totals so panel and page can
// supply their own action buttons while keeping the block order identical.
// ----------------------------------------------------------------------------
export function buildPrometResultHtml(
  data: TimeseriesResponse,
  opts: { interval: PrometInterval; actionsHtml?: string },
): string {
  const { interval, actionsHtml = '' } = opts;

  // Trend totals (hourly buckets are sum-safe because the request sends
  // downsample=none). These power the per_sensor "obdobje" lines even when
  // the user hasn't set Reference / Comparison pickers.
  const trendBySensor = sumBySensor(data.trend_series);
  const grandSensorTotal = sumValues(trendBySensor, (v) => v.motor);
  const days = bucketDayCount(data.trend_series);

  const sensorCount = data.per_sensor.length;
  const totalsBlock = `<div class="ngp-result-block">
      <h4>${PROMET_COPY.totalsTitle}${days > 0 ? ` (${days} ${days === 1 ? 'dan' : days < 5 ? 'dni' : 'dni'})` : ''}</h4>
      <div class="ngp-summary-row"><span>${PROMET_COPY.totalsSensorsLabel(sensorCount)}</span><span><strong>${fmt(grandSensorTotal)}</strong> ${PROMET_COPY.motorUnit}</span></div>
    </div>`;

  const sensorBlock = data.per_sensor.length === 0
    ? `<div class="ngp-result-block"><h4>${PROMET_COPY.perSensorTitle}</h4><div class="ngp-loading">${PROMET_COPY.empty}</div></div>`
    : `<div class="ngp-result-block">
        <h4>${PROMET_COPY.perSensorTitle} (${data.per_sensor.length})</h4>
        ${buildPerSensorTableHtml(data.per_sensor, trendBySensor)}
      </div>`;

  const trendBlock = `<div class="ngp-result-block">
      <h4>${PROMET_COPY.trendTitle} — ${interval === 'daily' ? PROMET_COPY.trendDaily : PROMET_COPY.trendHourly}</h4>
      <div class="ngp-chart" data-role="chart"></div>
      <div class="ngp-chart-empty" data-role="chart-empty" style="display:none;">${PROMET_COPY.chartEmpty}</div>
    </div>`;

  return totalsBlock + actionsHtml + sensorBlock + trendBlock;
}

export function buildPerSensorTableHtml(
  rows: PerSensorRow[],
  trendBySensor: Map<number, { motor: number; motor_lft: number; motor_rgt: number }>,
): string {
  // The first column holds the sensor's full name: it wraps (`ngp-name`),
  // the number columns stay on one line.
  const heads = [
    PROMET_COPY.tableSensor,
    PROMET_COPY.tableObdobje,
    PROMET_COPY.tableTotal,
    PROMET_COPY.tableRefAvg,
    PROMET_COPY.tableCmp,
    PROMET_COPY.tableDeltaAbs,
    PROMET_COPY.tableDeltaPct,
  ];
  const head = `<tr>${heads.map((h, i) => `<th class="${i === 0 ? 'ngp-name' : 'ngp-num'}">${h}</th>`).join('')}</tr>`;
  const body = rows.map((r) => {
    const t = trendBySensor.get(r.sensor_id);
    const obdobje = t ? `${fmt(t.motor_lft)} / ${fmt(t.motor_rgt)}` : '—';
    const total = t ? fmt(t.motor) : '—';
    const refAvg = r.reference ? fmt(r.reference.motor_avg) : '—';
    const cmp = r.comparison ? fmt(r.comparison.motor_total) : '—';
    const dAbs = r.delta ? signedFmt(r.delta.absolute) : '—';
    const dPct = r.delta ? signedPctFmt(r.delta.percent) : '—';
    const rowCls = r.direction_confirmed ? '' : ' class="ngp-row-warn"';
    const flag = r.direction_confirmed ? '' : ' ⚠';
    return `<tr${rowCls}>
      <td class="ngp-name">${escapeHtml(sensorLabel(r))}${flag}</td>
      <td class="ngp-num">${obdobje}</td>
      <td class="ngp-num"><strong>${total}</strong></td>
      <td class="ngp-num">${refAvg}</td>
      <td class="ngp-num">${cmp}</td>
      <td class="ngp-num">${dAbs}</td>
      <td class="ngp-num ${deltaPctCls(r.delta?.percent)}">${dPct}</td>
    </tr>`;
  }).join('');
  return `<div class="ngp-table-wrap"><table class="ngp-table"><thead>${head}</thead><tbody>${body}</tbody></table></div>`;
}

// ----------------------------------------------------------------------------
// ECharts trend chart option (pure). Per-sensor labels come from an injected
// resolver: the panel passes its probed-meta lookup; the standalone page builds
// the resolver from data.per_sensor (#359 F5).
// ----------------------------------------------------------------------------
export function prometTrendHasData(t: TrendSeriesShape): boolean {
  return t.by_sensor.some((s) => s.points.length > 0);
}

export function buildPrometChartOption(
  t: TrendSeriesShape,
  opts: {
    interval: PrometInterval;
    referenceRange: { from: string; to: string } | null;
    comparisonAt: string | null;
    sensorLabelForId: (sensorId: number) => string;
  },
): EChartsOption {
  const { interval, referenceRange, comparisonAt, sensorLabelForId } = opts;

  // First series carries the markArea + markLine so the band/line paint once.
  const markArea = referenceRange
    ? {
        itemStyle: { color: 'rgba(250, 204, 21, 0.18)' },
        data: [[{ name: PROMET_COPY.chartMarkRef, xAxis: referenceRange.from }, { xAxis: referenceRange.to }]],
      }
    : undefined;
  const markLine = comparisonAt
    ? {
        symbol: 'none',
        lineStyle: { color: '#dc2626', type: 'dashed' as const, width: 1.5 },
        label: { formatter: PROMET_COPY.chartMarkCmp, position: 'end' as const, fontSize: 10 },
        data: [{ xAxis: comparisonAt }],
      }
    : undefined;

  const series = t.by_sensor.map((s, idx) => {
    const base: Record<string, unknown> = {
      name: sensorLabelForId(s.sensor_id),
      type: 'line',
      smooth: true,
      showSymbol: false,
      data: s.points.map((p) => [p.ts, p.motor]),
    };
    // Attach markArea / markLine only to the first series to avoid
    // multi-paint of the same band/line.
    if (idx === 0) {
      if (markArea) base.markArea = markArea;
      if (markLine) base.markLine = markLine;
    }
    return base;
  });

  const granularity = interval === 'daily' ? 'date' : 'datetime';
  return {
    animation: false,
    // top: room for the legend row AND, under it, the »ref« / »primerjava«
    // marks — at 30 the marks were printed over the (now longer) legend names.
    // bottom: room for the zoom slider under the time axis (#720).
    grid: { left: 44, right: 16, top: 42, bottom: 58 },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'cross', label: { formatter: ljubljanaAxisPointerLabel() } },
      formatter: ljubljanaTooltipFormatter(granularity, (v) => (v == null ? '—' : fmt(v))),
    },
    // Sensor names are long: the legend shows a shortened name and gives the
    // full one in its tooltip (the axis tooltip and the table carry it too).
    legend: {
      type: 'scroll',
      top: 0,
      textStyle: { fontSize: 10 },
      formatter: legendName,
      tooltip: { show: true, formatter: (p: { name?: string }) => escapeHtml(p.name ?? '') },
    },
    // hideOverlap: on a phone-wide chart the day labels ran into each other.
    xAxis: {
      type: 'time',
      axisLabel: { fontSize: 10, hideOverlap: true, formatter: ljubljanaAxisLabel(granularity) },
    },
    yAxis: { type: 'value', axisLabel: { fontSize: 10 } },
    // Zoom and pan on the time axis (#720, Priloga 2 §3.4) — the zone chart's
    // shape: wheel / pinch / drag inside the plot, and a slider under it.
    dataZoom: [
      { type: 'inside', xAxisIndex: 0, filterMode: 'none' },
      {
        type: 'slider',
        xAxisIndex: 0,
        filterMode: 'none',
        height: 18,
        bottom: 8,
        labelFormatter: (value: number) => formatLjubljanaTs(new Date(value).toISOString(), 'date'),
      },
    ],
    series,
  } as EChartsOption;
}

// ----------------------------------------------------------------------------
// Daily rollup — pure transform, hourly buckets → per-day sums
// (Europe/Ljubljana calendar day).
// ----------------------------------------------------------------------------
export function rollupHourlyToDaily(t: TrendSeriesShape): TrendSeriesShape {
  // Group by Europe/Ljubljana calendar day (not the browser's local day), so
  // the daily totals are correct regardless of the viewer's machine timezone.
  const bucketKey = (ts: string) => formatLjubljanaTs(ts, 'date');
  const by_sensor = t.by_sensor.map((s) => {
    const acc = new Map<string, { motor: number; motor_lft: number; motor_rgt: number }>();
    for (const p of s.points) {
      const k = bucketKey(p.ts);
      const cur = acc.get(k) ?? { motor: 0, motor_lft: 0, motor_rgt: 0 };
      cur.motor += p.motor;
      cur.motor_lft += p.motor_lft;
      cur.motor_rgt += p.motor_rgt;
      acc.set(k, cur);
    }
    const points = [...acc.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([ts, v]) => ({ ts: ljubljanaDayStartIso(ts), motor: v.motor, motor_lft: v.motor_lft, motor_rgt: v.motor_rgt }));
    return { sensor_id: s.sensor_id, points };
  });
  const by_zone = t.by_zone.map((z) => {
    const acc = new Map<string, { in_count: number; out_count: number; total: number }>();
    for (const p of z.points) {
      const k = bucketKey(p.ts);
      const cur = acc.get(k) ?? { in_count: 0, out_count: 0, total: 0 };
      cur.in_count += p.in_count;
      cur.out_count += p.out_count;
      cur.total += p.total;
      acc.set(k, cur);
    }
    const points = [...acc.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([ts, v]) => ({ ts: ljubljanaDayStartIso(ts), ...v }));
    return { microzone_id: z.microzone_id, points };
  });
  const point_count = by_sensor.reduce((n, s) => n + s.points.length, 0)
    + by_zone.reduce((n, z) => n + z.points.length, 0);
  return {
    bucket_size_seconds: 86400,
    downsampled: t.downsampled,
    point_count,
    by_sensor,
    by_zone,
  };
}

// ----------------------------------------------------------------------------
// Labels + numeric formatting helpers
// ----------------------------------------------------------------------------
/**
 * How a sensor reads everywhere on screen: its own name (Telraam's, e.g.
 * »Sensor 12 - Cesta IX. korpusa, Mostovna«), marked when it was taken down.
 * Never our internal id — it differs from Telraam's number for some sensors —
 * and never the street alone: several streets carry two sensors.
 */
export function sensorLabel(s: Pick<PerSensorRow, 'sensor_name' | 'status'>): string {
  const name = s.sensor_name.trim() || UNKNOWN_SENSOR_LABEL;
  return `${name}${s.status === 'former' ? ` ${FORMER_SENSOR_SUFFIX}` : ''}`;
}

/** A legend entry: the name cut to LEGEND_NAME_MAX characters, »…« when cut. */
export function legendName(name: string): string {
  return name.length > LEGEND_NAME_MAX ? `${name.slice(0, LEGEND_NAME_MAX - 1).trimEnd()}…` : name;
}

/**
 * The shown range as people read it: day-first, both days inclusive
 * (»1. 7. 2026 – 7. 7. 2026«). `range.to` is the exclusive end — the midnight
 * after the last day — so the last day is the day of the instant before it.
 */
export function sensorRangeLabel(range: { from: string; to: string }): string {
  const first = formatLjubljanaTs(range.from, 'date');
  const end = new Date(range.to).getTime();
  const last = Number.isFinite(end) ? formatLjubljanaTs(new Date(end - 1).toISOString(), 'date') : '';
  return `${formatDayFirst(first)} – ${formatDayFirst(last)}`;
}

function sumBySensor(t: TrendSeriesShape): Map<number, { motor: number; motor_lft: number; motor_rgt: number }> {
  const out = new Map<number, { motor: number; motor_lft: number; motor_rgt: number }>();
  for (const s of t.by_sensor) {
    let motor = 0, motor_lft = 0, motor_rgt = 0;
    for (const p of s.points) { motor += p.motor; motor_lft += p.motor_lft; motor_rgt += p.motor_rgt; }
    out.set(s.sensor_id, { motor, motor_lft, motor_rgt });
  }
  return out;
}

function sumValues<V>(m: Map<number, V>, pick: (v: V) => number): number {
  let s = 0;
  for (const v of m.values()) s += pick(v);
  return s;
}

/**
 * How many Europe/Ljubljana calendar days the buckets touch (the UTC date of
 * a bucket is the wrong day for the hours around midnight). The sensors share
 * their hours, so each DISTINCT timestamp is converted once — a month of 22
 * sensors is 744 conversions, not 16,368.
 */
export function bucketDayCount(t: TrendSeriesShape): number {
  const stamps = new Set<string>();
  for (const s of t.by_sensor) for (const p of s.points) stamps.add(p.ts);
  const days = new Set<string>();
  for (const ts of stamps) days.add(formatLjubljanaTs(ts, 'date'));
  return days.size;
}

function signedFmt(n: number): string {
  if (!Number.isFinite(n)) return '—';
  const sign = n >= 0 ? '+' : '';
  return sign + fmt(n);
}

function signedPctFmt(n: number): string {
  if (!Number.isFinite(n)) return '—';
  const sign = n >= 0 ? '+' : '';
  return `${sign}${n.toFixed(1)}%`;
}

function deltaPctCls(pct: number | undefined): string {
  if (pct === undefined || !Number.isFinite(pct)) return '';
  if (pct > 0.1) return 'ngp-pct-pos';
  if (pct < -0.1) return 'ngp-pct-neg';
  return '';
}

/**
 * A number of vehicles: whole, like the zone view prints them (#720). Sums of
 * hourly estimates and averages carry fractions (»834.403,7«) that mean nothing
 * for a count. The Δ % has its own formatter and keeps its decimal.
 */
function fmt(n: number): string {
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString('sl-SI', { maximumFractionDigits: 0 });
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
